import React, { useState, useRef } from "react";
import { Drawer, Box, Typography, Chip, Fade } from "@mui/material";
import { Person } from "@mui/icons-material";

interface Member {
  name: string;
  inst: string;
  img: string;
  text: string;
}

interface MemberDrawerProps {
  open: boolean;
  handleClose: () => void;
  member: Member | null;
}

const MemberDrawer: React.FC<MemberDrawerProps> = ({
  open,
  handleClose,
  member,
}) => {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [dragStartY, setDragStartY] = useState(0);
  const [dragCurrentY, setDragCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    setDragStartY(e.touches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - dragStartY;

    if (deltaY > 0) {
      // 下方向のドラッグのみ
      setDragCurrentY(deltaY);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;

    // 100px以上ドラッグしたら閉じる
    if (dragCurrentY > 100) {
      handleClose();
    }

    setIsDragging(false);
    setDragCurrentY(0);
    setDragStartY(0);
  };

  if (!member) return null;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDrawer-paper": {
          height: "85vh",
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          margin: 0,
          padding: 0,
          background: "linear-gradient(135deg, #f8fafa 0%, #e9f0ee 100%)",
          transform: isDragging
            ? `translateY(${dragCurrentY}px)`
            : "translateY(0px)",
          transition: isDragging ? "none" : "transform 0.3s ease-out",
        },
      }}
    >
      <Box
        ref={drawerRef}
        sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* ドラッグハンドル */}
        <Box
          sx={{
            width: 40,
            height: 4,
            bgcolor: "rgba(83, 105, 109, 0.4)",
            borderRadius: 2,
            mx: "auto",
            my: 2,
            cursor: "grab",
            flexShrink: 0,
            "&:active": {
              bgcolor: "rgba(83, 105, 109, 0.6)",
            },
          }}
        />

        {/* ヘッダーとプロフィール画像セクション */}
        <Box sx={{ px: 3, pb: 3, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
            <Box sx={{ mr: 3 }}>
              <Box
                sx={{
                  position: "relative",
                  width: 120,
                  height: 120,
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 8px 24px rgba(83, 105, 109, 0.15)",
                  bgcolor: "#f5f5f5",
                  border: "3px solid #ffffff",
                }}
              >
                <Fade in={imageLoaded} timeout={600}>
                  <Box
                    component="img"
                    src={member.img}
                    alt={member.name}
                    onLoad={() => setImageLoaded(true)}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition:
                        member.name === "吉田雪乃"
                          ? "center top"
                          : "center center",
                      position: "absolute",
                      top: 0,
                      left: 0,
                    }}
                    {...({ loading: "lazy", decoding: "async" } as any)}
                  />
                </Fade>
                {!imageLoaded && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      color: "#b4bdbb",
                    }}
                  >
                    <Person sx={{ fontSize: 48 }} />
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ flex: 1, pt: 1 }}>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{ color: "#53696d", mb: 1, fontFamily: "serif" }}
              >
                {member.name}
              </Typography>
              <Chip
                label={member.inst}
                size="medium"
                sx={{
                  bgcolor: "#ffffff",
                  color: "#607d7d",
                  fontWeight: "medium",
                  boxShadow: "0 2px 4px rgba(83, 105, 109, 0.1)",
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* スクロール可能なテキストコンテンツ */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: 3,
            pb: 3,
            "&::-webkit-scrollbar": {
              width: 6,
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(83, 105, 109, 0.3)",
              borderRadius: 3,
            },
          }}
        >
          <Box
            sx={{
              bgcolor: "#ffffff",
              borderRadius: 2,
              p: 3,
              boxShadow: "0 4px 12px rgba(83, 105, 109, 0.08)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-line",
                color: "#2c3f42",
                lineHeight: 1.8,
                textAlign: "justify",
              }}
            >
              {member.text}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default MemberDrawer;
